import { statsigClientProvider } from 'StatsigClientProvider';
import { type FellowshipEntry, type FellowshipOverridesConfigValue } from './Scholarship.types';

// Keep this name in sync with AdmissionOffer::FELLOWSHIP_OVERRIDES_CONFIG on the server.
export const FELLOWSHIP_OVERRIDES_CONFIG = 'scholarship_label_overrides_by_utm_campaign';

// The tier suffix (-T0, -T1, ...) is a geography split that never changes the fellowship,
// so campaign keys in the config normally omit it. \d+ rather than [0-3] so a future T4 still resolves.
const TIER_SUFFIX = /-T\d+$/;

export function resolveFellowshipOverride(
    configValue: FellowshipOverridesConfigValue | null | undefined,
    utmCampaign: string | null | undefined,
    programType: string | null | undefined,
): FellowshipEntry | null {
    if (!configValue || !utmCampaign || !programType) return null;

    // An exact key wins, so a campaign whose format does not follow the segment-tier
    // convention only needs its full utm_campaign added to the config — a data change,
    // no deploy. The tier strip is a fallback that keeps the standard `{segment}-T{n}`
    // campaigns down to one config row per segment.
    const campaigns = configValue.campaigns;
    const fellowshipKey = campaigns?.[utmCampaign] ?? campaigns?.[utmCampaign.replace(TIER_SUFFIX, '')];
    if (!fellowshipKey) return null;

    const fellowship = configValue.fellowships?.[fellowshipKey];
    if (!fellowship?.label || !fellowship.description || !fellowship.program_type) return null;
    if (fellowship.program_type !== programType) return null;

    return fellowship;
}

export async function getFellowshipOverride(
    utmCampaign: string | null | undefined,
    programType: string | null | undefined,
): Promise<FellowshipEntry | null> {
    if (!utmCampaign || !programType) return null;

    const dynamicConfig = await statsigClientProvider.waitForDynamicConfig(FELLOWSHIP_OVERRIDES_CONFIG);
    if (!dynamicConfig) return null;

    return resolveFellowshipOverride(dynamicConfig.value as FellowshipOverridesConfigValue, utmCampaign, programType);
}
